/**
 * 500's deck and deal plan (rules §1.1, §2.1).
 *
 * 43 cards: red suits keep the 4, black suits do not, plus one Joker.
 *   ♥ A K Q J 10 9 8 7 6 5 4   (11)
 *   ♦ A K Q J 10 9 8 7 6 5 4   (11)
 *   ♠ A K Q J 10 9 8 7 6 5     (10)
 *   ♣ A K Q J 10 9 8 7 6 5     (10)
 *   Joker                       (1)
 *
 * The consequence that matters to the bot: a black trump suit has 12 trumps in
 * circulation and a red one has 13 (rules §4.2 derivation, edge #21).
 */
import { SUITS, cardId, colourOf } from '../../core/cards.js';
import { kittySize } from './config.js';
/** Lowest rank present in each suit for a given deck variant. */
export function lowestRank(suit, config) {
    if (config.deckVariant === '45')
        return 4;
    return colourOf(suit) === 'red' ? 4 : 5;
}
/** Canonical (pre-shuffle) order. The fairness commitment hashes this ordering,
 *  so it must be stable forever: suits in core SUITS order, ranks ascending,
 *  Joker last. */
export function fhDeck(config) {
    const cards = [];
    for (const suit of SUITS) {
        const low = lowestRank(suit, config);
        for (let r = low; r <= 14; r++)
            cards.push(cardId(r, suit));
    }
    cards.push('JOKER');
    return Object.freeze(cards);
}
/** 3 – 4 – 3 to each player with one kitty card after each round (rules §2.1).
 *  The interleaving is cosmetic to the rules but canonical for verifiability:
 *  the committed permutation is consumed front-to-back in exactly this order. */
export function fhDealPlan(config) {
    const k = kittySize(config);
    // Spread the kitty over the three rounds as evenly as possible so the 45-card
    // variant (kitty 5) keeps the same shape rather than needing its own table.
    const per = [Math.ceil(k / 3), Math.ceil((k - Math.ceil(k / 3)) / 2)];
    const third = k - per[0] - per[1];
    return {
        steps: [
            { each: 3 }, { kitty: per[0] },
            { each: 4 }, { kitty: per[1] },
            { each: 3 }, { kitty: third },
        ],
        // The player to the dealer's left receives the first card and bids first
        // (rules §2.1, §3.1.1).
        firstSeatOffset: 1,
    };
}
/** How many cards of `suit` exist in the deck (used by the bot's inference over
 *  outstanding cards, and by the trump-count heuristics). */
export function suitLength(suit, config) {
    return 15 - lowestRank(suit, config);
}
/** Total trumps in circulation when `trump` is named: the suit itself, plus the
 *  borrowed left bower, plus the Joker (rules §4.2 — 13 for red, 12 for black). */
export function trumpCountInDeck(trump, config) {
    return suitLength(trump, config) + 2;
}
//# sourceMappingURL=deck.js.map